// Low-level fetch helpers. Two surfaces:
//   • appView()  — public Bluesky AppView reads, straight from the browser
//                  (no auth, CORS-open). Used for all media search/fetch.
//   • api*()     — our worker (/api/*): auth, identity, the gg game API.
// No store imports here so this stays a leaf module; callers pass the bearer.

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')
const APPVIEW_BASE = 'https://public.api.bsky.app'
const TIMEOUT_MS = 15_000

function withTimeout(): AbortSignal {
  const c = new AbortController()
  setTimeout(() => c.abort(), TIMEOUT_MS)
  return c.signal
}

async function parseError(r: Response): Promise<string> {
  const text = await r.text().catch(() => '')
  try {
    const j = JSON.parse(text)
    return j.error ?? j.message ?? text
  } catch {
    return text || r.statusText
  }
}

/** Public AppView read (no auth). Retries once on transient 5xx. */
export async function appView<T>(path: string): Promise<T> {
  const url = `${APPVIEW_BASE}/xrpc/${path}`
  const once = async (): Promise<T> => {
    const r = await fetch(url, { signal: withTimeout() })
    if (!r.ok) throw new Error(`${r.status} ${await parseError(r)}`)
    return (await r.json()) as T
  }
  try {
    return await once()
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    if (/\b(502|503|504|524)\b/.test(msg)) {
      await new Promise((r) => setTimeout(r, 1500))
      return once()
    }
    throw e
  }
}

/** GET our worker API. */
export async function apiGet<T>(path: string, token?: string | null): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: withTimeout(),
  })
  if (!r.ok) throw new Error(await parseError(r))
  return (await r.json()) as T
}

/** Send (POST/PUT/DELETE) to our worker API with an optional bearer. */
export async function apiSend<T>(
  path: string,
  opts: { method?: 'POST' | 'PUT' | 'DELETE'; body?: unknown; token?: string | null } = {},
): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: withTimeout(),
  })
  if (!r.ok) throw new Error(await parseError(r))
  return (await r.json()) as T
}

/** Absolute URL for an OAuth redirect start (full navigation, not fetch). */
export function apiHref(path: string): string {
  return `${API_BASE}${path}`
}
